import type {
  Tag,
  TagCategory,
  TagRegistry,
  TagTreeNode,
  PathStyle,
  MarkerStyle,
  Position,
} from '../types/position.ts';

export const MAX_TAG_DEPTH = 4;
export const SHADE_BAND: readonly [number, number] = [0.15, 0.85];

function compareSiblings(a: Tag, b: Tag): number {
  return a.sort_order - b.sort_order || a.tag.localeCompare(b.tag) || a.id - b.id;
}

export function buildRegistry(
  categories: TagCategory[],
  tags: Tag[],
  version = 0,
): TagRegistry {
  const cats = new Map<number, TagCategory>();
  categories.forEach(c => cats.set(c.id, c));

  const tagMap = new Map<number, Tag>();
  tags.forEach(t => tagMap.set(t.id, t));

  const childrenOf = new Map<number | null, Tag[]>();
  const rootsOf = new Map<number, Tag[]>();

  const sortedTags = [...tags].sort(compareSiblings);
  for (const t of sortedTags) {
    const parentKey: number | null = t.parent_tag_id;
    if (!childrenOf.has(parentKey)) childrenOf.set(parentKey, []);
    childrenOf.get(parentKey)!.push(t);
    if (parentKey === null) {
      if (!rootsOf.has(t.category_id)) rootsOf.set(t.category_id, []);
      rootsOf.get(t.category_id)!.push(t);
    }
  }

  const effectiveCategories = categories.filter(
    c => c.scope === 'user' || !c.shadowed_by_me
  );

  return {
    categories: cats,
    tags: tagMap,
    childrenOf,
    rootsOf,
    effectiveCategories,
    version,
  };
}

export function buildTagTree(tags: Tag[]): TagTreeNode[] {
  const sorted = [...tags].sort(compareSiblings);
  const byId = new Map<number, TagTreeNode>();
  sorted.forEach(t => byId.set(t.id, { tag: t, children: [] }));
  const roots: TagTreeNode[] = [];
  for (const t of sorted) {
    const node = byId.get(t.id)!;
    if (t.parent_tag_id != null && byId.has(t.parent_tag_id)) {
      byId.get(t.parent_tag_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export function getTagInCategory(
  pos: Position,
  categoryId: number,
  registry: TagRegistry,
): Tag | null {
  if (!pos.tag_ids || pos.tag_ids.length === 0) return null;
  for (const id of pos.tag_ids) {
    const t = registry.tags.get(id);
    if (t && t.category_id === categoryId) return t;
  }
  return null;
}

export function depthOf(tag: Tag, registry: TagRegistry): number {
  let d = 0;
  let cur: Tag | undefined = tag;
  while (cur && cur.parent_tag_id != null) {
    const parent = registry.tags.get(cur.parent_tag_id);
    if (!parent) break;
    cur = parent;
    d++;
    if (d > 128) break;
  }
  return d;
}

export function ancestorChain(tag: Tag, registry: TagRegistry): Tag[] {
  const chain: Tag[] = [];
  let cur: Tag | undefined = tag;
  const seen = new Set<number>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur);
    if (cur.parent_tag_id == null) break;
    cur = registry.tags.get(cur.parent_tag_id);
  }
  return chain;
}

export function getAncestorAtLevel(
  tag: Tag | null,
  level: number | 'leaf',
  registry: TagRegistry,
): Tag | null {
  if (!tag) return null;
  if (level === 'leaf') return tag;
  const chain = ancestorChain(tag, registry);
  if (level >= chain.length) return chain[chain.length - 1] ?? tag;
  return chain[level];
}

// -----------------------------------------------------------------------------
// Style resolution with inheritance + shaded children
// -----------------------------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3) * 255, hue2rgb(p, q, h) * 255, hue2rgb(p, q, h - 1 / 3) * 255];
}

// Returns baseHex with the given lightness, keeping its hue and saturation.
export function withLightness(baseHex: string, lightness: number): string {
  const rgb = hexToRgb(baseHex);
  if (!rgb) return baseHex;
  const [h, s] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const [r2, g2, b2] = hslToRgb(h, s, lightness);
  return rgbToHex(r2, g2, b2);
}

interface ShadeChannel {
  name: string;
  own: (tag: Tag) => boolean;
  flag: (tag: Tag) => boolean | undefined;
}

const PATH_SHADE: ShadeChannel = {
  name: 'path',
  own: (t) => t.path_style?.color != null,
  flag: (t) => t.path_style?.shadeChildren,
};
const FILL_SHADE: ShadeChannel = {
  name: 'fill',
  own: (t) => t.marker_style?.fillColor != null,
  flag: (t) => t.marker_style?.shadeChildren,
};
const BORDER_SHADE: ShadeChannel = {
  name: 'border',
  own: (t) => t.marker_style?.borderColor != null,
  flag: (t) => t.marker_style?.shadeChildren,
};

interface ShadeAnchor {
  tag: Tag;
  base: string | undefined;
  shade: boolean;
}

const layoutCache = new WeakMap<TagRegistry, Map<string, Map<number, number>>>();

// Lightness of every member of an anchor's shade group. Leaves share the band in tree order
// (first is lightest); an inner member takes the midpoint of its leaves.
function shadeLightness(anchor: ShadeAnchor, channel: ShadeChannel, registry: TagRegistry): Map<number, number> {
  let cache = layoutCache.get(registry);
  if (!cache) {
    cache = new Map();
    layoutCache.set(registry, cache);
  }
  const key = `${channel.name}:${anchor.tag.id}:${anchor.shade}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const membersOf = (tag: Tag, shade: boolean): Tag[] =>
    shade ? (registry.childrenOf.get(tag.id) ?? []).filter(c => !channel.own(c)) : [];

  const spans = new Map<number, [number, number]>();
  let leafCount = 0;
  const place = (tag: Tag, shade: boolean): [number, number] => {
    const members = membersOf(tag, shade);
    let span: [number, number];
    if (members.length === 0) {
      span = [leafCount, leafCount];
      leafCount++;
    } else {
      let first = Infinity;
      let last = -Infinity;
      for (const child of members) {
        const [a, b] = place(child, channel.flag(child) ?? shade);
        first = Math.min(first, a);
        last = Math.max(last, b);
      }
      span = [first, last];
    }
    spans.set(tag.id, span);
    return span;
  };
  for (const child of membersOf(anchor.tag, anchor.shade)) {
    place(child, channel.flag(child) ?? anchor.shade);
  }

  const [lo, hi] = SHADE_BAND;
  const slot = (i: number) => hi - ((i + 0.5) / leafCount) * (hi - lo);
  const out = new Map<number, number>();
  for (const [id, [a, b]] of spans) out.set(id, (slot(a) + slot(b)) / 2);
  cache.set(key, out);
  return out;
}

function shadedColor(anchor: ShadeAnchor | null, tag: Tag, channel: ShadeChannel, registry: TagRegistry): string | null {
  if (!anchor?.base) return null;
  const lightness = shadeLightness(anchor, channel, registry).get(tag.id);
  return lightness == null ? null : withLightness(anchor.base, lightness);
}

export interface DefaultStyles {
  path: PathStyle;
  marker: MarkerStyle;
}

export type StyleOrigin = 'own' | 'inherited' | 'shaded' | 'default';

const PATH_KEYS = ['type', 'color', 'opacity', 'weight', 'dash'] as const;
const MARKER_KEYS = [
  'type', 'radius', 'fillType', 'fillColor', 'fillOpacity',
  'borderType', 'borderColor', 'borderOpacity', 'borderWeight', 'borderDash',
] as const;

export interface ResolvedStyle extends DefaultStyles {
  shade: { path: boolean; marker: boolean };
  origins: {
    path: Record<(typeof PATH_KEYS)[number], StyleOrigin>;
    marker: Record<(typeof MARKER_KEYS)[number], StyleOrigin>;
  };
}

function originFor(own: boolean, shaded: boolean, prev: StyleOrigin): StyleOrigin {
  if (own) return 'own';
  if (shaded) return 'shaded';
  return prev !== 'default' ? 'inherited' : 'default';
}

// Walks root → tag. A shade flag set on a tag (true or false) decides for that
// tag; when unset it follows the direct parent's effective flag, and a root
// falls back to the default flag.
export function resolveStyleDetail(
  stylingTag: Tag | null,
  registry: TagRegistry,
  defaults: DefaultStyles,
): ResolvedStyle {
  const chain = stylingTag ? ancestorChain(stylingTag, registry) : [];
  let path: PathStyle = { ...defaults.path };
  let marker: MarkerStyle = { ...defaults.marker };
  const pathOrigin = Object.fromEntries(PATH_KEYS.map(k => [k, 'default'])) as ResolvedStyle['origins']['path'];
  const markerOrigin = Object.fromEntries(MARKER_KEYS.map(k => [k, 'default'])) as ResolvedStyle['origins']['marker'];
  let pathShade = defaults.path.shadeChildren ?? false;
  let markerShade = defaults.marker.shadeChildren ?? false;
  let pathAnchor: ShadeAnchor | null = null;
  let fillAnchor: ShadeAnchor | null = null;
  let borderAnchor: ShadeAnchor | null = null;

  chain.forEach((cur, i) => {
    const parent = i > 0 ? chain[i - 1] : null;
    const shadedPath = parent && pathShade && !PATH_SHADE.own(cur)
      ? shadedColor(pathAnchor, cur, PATH_SHADE, registry) : null;
    const shadedFill = parent && markerShade && !FILL_SHADE.own(cur)
      ? shadedColor(fillAnchor, cur, FILL_SHADE, registry) : null;
    const shadedBorder = parent && markerShade && !BORDER_SHADE.own(cur)
      ? shadedColor(borderAnchor, cur, BORDER_SHADE, registry) : null;

    for (const k of PATH_KEYS) {
      pathOrigin[k] = originFor(cur.path_style?.[k] != null, k === 'color' && shadedPath != null, pathOrigin[k]);
    }
    for (const k of MARKER_KEYS) {
      markerOrigin[k] = originFor(
        cur.marker_style?.[k] != null,
        (k === 'fillColor' && shadedFill != null) || (k === 'borderColor' && shadedBorder != null),
        markerOrigin[k],
      );
    }

    path = { ...path, ...cur.path_style };
    marker = { ...marker, ...cur.marker_style };
    if (shadedPath) path.color = shadedPath;
    if (shadedFill) marker.fillColor = shadedFill;
    if (shadedBorder) marker.borderColor = shadedBorder;

    pathShade = cur.path_style?.shadeChildren ?? pathShade;
    markerShade = cur.marker_style?.shadeChildren ?? markerShade;
    if (!shadedPath) pathAnchor = { tag: cur, base: path.color, shade: pathShade };
    if (!shadedFill) fillAnchor = { tag: cur, base: marker.fillColor, shade: markerShade };
    if (!shadedBorder) borderAnchor = { tag: cur, base: marker.borderColor, shade: markerShade };
  });

  return {
    path,
    marker,
    shade: { path: pathShade, marker: markerShade },
    origins: { path: pathOrigin, marker: markerOrigin },
  };
}

export function resolveEffectiveStyle(
  stylingTag: Tag | null,
  registry: TagRegistry,
  defaults: DefaultStyles,
): DefaultStyles {
  const { path, marker } = resolveStyleDetail(stylingTag, registry, defaults);
  return { path, marker };
}

// Defaults carry no aggregate value, so only the tag chain decides.
const NO_DEFAULTS: DefaultStyles = { path: {}, marker: {} };

export function aggregatedTagIds(registry: TagRegistry): number[] {
  return Array.from(registry.tags.values())
    .filter(t => resolveEffectiveStyle(t, registry, NO_DEFAULTS).path.aggregate === true)
    .map(t => t.id);
}

// Registry with `draft` placed among its siblings the way the server places it.
export function withPlacedTag(registry: TagRegistry, draft: Tag, requested: number | null): TagRegistry {
  const siblings = (draft.parent_tag_id == null
    ? registry.rootsOf.get(draft.category_id)
    : registry.childrenOf.get(draft.parent_tag_id)) ?? [];
  const others = siblings.filter(t => t.id !== draft.id);
  const at = requested == null ? others.length : others.findIndex(t => t.sort_order >= requested);
  const ordered = [...others];
  ordered.splice(at < 0 ? others.length : at, 0, draft);
  const replaced = new Set([draft.id, ...ordered.map(t => t.id)]);
  const tags = [
    ...Array.from(registry.tags.values()).filter(t => !replaced.has(t.id)),
    ...ordered.map((t, i) => ({ ...t, sort_order: i })),
  ];
  return buildRegistry(Array.from(registry.categories.values()), tags, registry.version);
}

export function dashArrayFor(type: PathStyle['type'], dash?: string): string | undefined {
  switch (type) {
    case 'dashed': return dash ?? '5,5';
    case 'dotted': return '1,4';
    case 'dash-dot': return '8,4,1,4';
    default: return undefined;
  }
}

export function subtreeIds(tagId: number, registry: TagRegistry): Set<number> {
  const out = new Set<number>();
  const stack = [tagId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const child of registry.childrenOf.get(id) ?? []) stack.push(child.id);
  }
  return out;
}

export function tagHeight(tag: Tag, registry: TagRegistry): number {
  const kids = registry.childrenOf.get(tag.id) ?? [];
  if (kids.length === 0) return 0;
  return 1 + Math.max(...kids.map(k => tagHeight(k, registry)));
}

export function canAddChild(parent: Tag, registry: TagRegistry): boolean {
  return depthOf(parent, registry) + 1 <= MAX_TAG_DEPTH - 1;
}

// Keeps only selected tags whose ancestors are not also selected.
export function topMostTags(ids: number[], registry: TagRegistry): Tag[] {
  const selected = new Set(ids);
  const out: Tag[] = [];
  for (const id of new Set(ids)) {
    const tag = registry.tags.get(id);
    if (!tag) continue;
    const hasSelectedAncestor = ancestorChain(tag, registry).slice(0, -1).some(a => selected.has(a.id));
    if (!hasSelectedAncestor) out.push(tag);
  }
  return out;
}

// Returns the single category all selected tags belong to, or null if mixed or empty.
export function selectionCategoryId(ids: number[], registry: TagRegistry): number | null {
  const categories = new Set<number>();
  for (const id of ids) {
    const tag = registry.tags.get(id);
    if (!tag) return null;
    categories.add(tag.category_id);
  }
  if (categories.size !== 1) return null;
  return categories.values().next().value ?? null;
}

// `target` null means the category root. `moving` is the top-most set being moved (may be empty for a new tag).
export function parentProblem(target: Tag | null, moving: Tag[], registry: TagRegistry): string | null {
  if (target && moving.some(m => subtreeIds(m.id, registry).has(target.id))) {
    return 'A tag cannot be moved under itself or one of its children.';
  }
  const newDepth = target ? depthOf(target, registry) + 1 : 0;
  const deepest = Math.max(0, ...moving.map(m => tagHeight(m, registry)));
  if (newDepth + deepest > MAX_TAG_DEPTH - 1) {
    return `Would exceed the maximum tag depth of ${MAX_TAG_DEPTH}.`;
  }
  return null;
}

export function flattenTagTree(tags: Tag[]): { tag: Tag; depth: number }[] {
  const out: { tag: Tag; depth: number }[] = [];
  const walk = (nodes: TagTreeNode[], depth: number) => {
    for (const node of nodes) {
      out.push({ tag: node.tag, depth });
      walk(node.children, depth + 1);
    }
  };
  walk(buildTagTree(tags), 0);
  return out;
}

export type ScopeFilter = 'own' | 'global' | 'both';

export interface TagFilter {
  categoryText: string;
  tagText: string;
  scope: ScopeFilter;
}

export type TagRow =
  | { kind: 'category'; key: string; category: TagCategory; expanded: boolean; dimmed: boolean; shadowedName: string | null }
  | { kind: 'tag'; key: string; tag: Tag; category: TagCategory; depth: number; expanded: boolean; hasChildren: boolean; dimmed: boolean }
  | { kind: 'empty'; key: string; category: TagCategory };

export function categoryKey(id: number): string {
  return `c:${id}`;
}

export function tagKey(id: number): string {
  return `t:${id}`;
}

function scopeAllows(cat: TagCategory, scope: ScopeFilter): boolean {
  if (scope === 'both') return true;
  return scope === 'own' ? cat.scope === 'user' : cat.scope === 'global';
}

function categoryTextMatches(cat: TagCategory, text: string, registry: TagRegistry): boolean {
  if (!text) return true;
  if (cat.tag_category.toLowerCase().includes(text)) return true;
  if (cat.scope === 'user' && cat.shadows_category_id != null) {
    const shadowed = registry.categories.get(cat.shadows_category_id);
    return !!shadowed && shadowed.tag_category.toLowerCase().includes(text);
  }
  return false;
}

// `expanded` holds categoryKey/tagKey values the user opened. Filters force-open matched paths.
export function buildTagRows(
  registry: TagRegistry,
  filter: TagFilter,
  expanded: ReadonlySet<string>,
): TagRow[] {
  const categoryText = filter.categoryText.trim().toLowerCase();
  const tagText = filter.tagText.trim().toLowerCase();

  const matches = new Set<number>();
  if (tagText) {
    for (const tag of registry.tags.values()) {
      if (tag.tag.toLowerCase().includes(tagText)) matches.add(tag.id);
    }
  }
  const forced = new Set<number>();
  for (const id of matches) {
    for (const t of ancestorChain(registry.tags.get(id)!, registry)) forced.add(t.id);
  }

  const categories = Array.from(registry.categories.values()).sort((a, b) => {
    if (a.scope !== b.scope) return a.scope === 'user' ? -1 : 1;
    return a.tag_category.localeCompare(b.tag_category);
  });

  const rows: TagRow[] = [];
  for (const cat of categories) {
    if (!scopeAllows(cat, filter.scope)) continue;
    if (!categoryTextMatches(cat, categoryText, registry)) continue;
    if (tagText && !Array.from(matches).some(id => registry.tags.get(id)!.category_id === cat.id)) continue;

    const dimmed = cat.scope === 'global' && cat.shadowed_by_me;
    const catKey = categoryKey(cat.id);
    const catExpanded = !!tagText || expanded.has(catKey);
    const shadowedName = cat.shadows_category_id != null
      ? registry.categories.get(cat.shadows_category_id)?.tag_category ?? null
      : null;
    rows.push({ kind: 'category', key: catKey, category: cat, expanded: catExpanded, dimmed, shadowedName });
    if (!catExpanded) continue;

    const roots = registry.rootsOf.get(cat.id) ?? [];
    if (roots.length === 0) {
      rows.push({ kind: 'empty', key: `e:${cat.id}`, category: cat });
      continue;
    }

    const emit = (tag: Tag, depth: number, underMatch: boolean) => {
      const isVisible = !tagText || underMatch || matches.has(tag.id) || forced.has(tag.id);
      if (!isVisible) return;
      const kids = registry.childrenOf.get(tag.id) ?? [];
      const key = tagKey(tag.id);
      const open = !!tagText || expanded.has(key);
      rows.push({
        kind: 'tag', key, tag, category: cat, depth, expanded: open,
        hasChildren: kids.length > 0, dimmed,
      });
      if (!open) return;
      const childUnderMatch = underMatch || matches.has(tag.id);
      for (const kid of kids) emit(kid, depth + 1, childUnderMatch);
    };
    for (const root of roots) emit(root, 0, false);
  }
  return rows;
}

